import { useFlag } from '@calendly/feature-check-react'
import { useOnValueChange, useQueryParams } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { ArrowChevronLeftMiniIcon } from '@calendly/ui/components/icons'
import { EyebrowBackButton, PageHeader } from '@calendly/ui/components/page-header'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setGetParams } from 'actions/core/navigation'
import { changedSearchFilters } from 'components/meetings'
import { mapChangedFiltersForAnalytics } from 'features/meetings/utils'
import { useRightRail } from 'features/right_rail'
import { TRACKING_EVENT_NAMES, useTrack, useTrackPageExit } from 'hooks/analytics_events'
import { useScope } from 'hooks/meetings/use_scope'
import { timezoneSelector } from 'selectors/current_user'
import { isCalendlyMeeting } from 'shared/utils'

import { Header } from './header'
import { List } from './list'
import { useGetMeetingsSupergraphQuery, useSearchMeetingsQuery } from '../../graphql_api'
import {
  SEARCH_IN,
  SORT_OPTIONS,
} from '../constants'
import {
  buildMeetingsSearchSupergraphInput,
  convertSupergraphSearchResponse,
} from '../search_supergraph_utils'
import { convertSearchResponse, useToPrepareSearchFiltersParams, getMatchTypeFilters } from '../utils'

const useSearchResultsQuery = ({ query, sortBy, filters }) => {
  const filterParams = useToPrepareSearchFiltersParams(filters)
  const { scopeType, scopeId } = useScope()
  const track = useTrack()
  const supergraphSearch = useFlag('sa_meetings_page_with_supergraph', false)
  const timezone = useSelector(timezoneSelector)
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker', false)

  const supergraphInput = useMemo(
    () => buildMeetingsSearchSupergraphInput({
      query,
      sortBy,
      preparedFilter: filterParams,
      scopeType,
      scopeId,
    }),
    [
      query,
      sortBy,
      filterParams,
      scopeType,
      scopeId,
    ],
  )

  const legacyResult = useSearchMeetingsQuery(
    {
      query,
      orderBy: { prioritize: sortBy },
      findBestCharForPassages: true,
      isOrganizer: false,
      includeNotetakerIneligibleExternalEvents: true,
      ...filterParams,
    },
    { skip: supergraphSearch },
  )

  const supergraphResult = useGetMeetingsSupergraphQuery(supergraphInput, {
    skip: !supergraphSearch,
  })

  const data = supergraphSearch ? supergraphResult.data : legacyResult.data
  const isLoading = supergraphSearch ? supergraphResult.isLoading : legacyResult.isLoading
  const isError = supergraphSearch ? supergraphResult.isError : legacyResult.isError

  const changedFilters = changedSearchFilters(filters)

  const meetings = supergraphSearch
    ? convertSupergraphSearchResponse(data, timezone, hasMsTeamsFlag)?.data || []
    : convertSearchResponse(data)?.data || []
  useOnValueChange(data, () => {
    track({
      name: TRACKING_EVENT_NAMES.SEARCH_PERFORMED,
      properties: {
        search_text: query,
        results_count: meetings.length,
        search_type: 'text',
        location: 'meetings_search_results',
        filters: mapChangedFiltersForAnalytics(changedFilters),
        semantic_search: supergraphSearch ? 'supergraph_keyword' : 'include_related_terms',
        match_type: getMatchTypeFilters(filterParams),
      }
    })
  })

  return {
    data: meetings,
    isLoading,
    isError,
  }
}

const useFilters = () => {
  const dispatch = useDispatch()
  const {
    matches, period, start, end, invitees, event_types: eventTypes, status, scheduling_method: schedulingMethod, role,
    utm_campaign: utmCampaign, utm_content: utmContent, utm_medium: utmMedium, utm_source: utmSource, utm_term: utmTerm, query
  } = useQueryParams()

  const filters = {
    searchIn: Array.from(matches || []).filter((m) => SEARCH_IN.includes(m)),
    dateRange: {
      period,
      start,
      end,
    },
    inviteeEmails: Array.from(invitees || []),
    eventTypeIds: Array.from(eventTypes || []),
    status: Array.from(status || []),
    schedulingMethod: Array.from(schedulingMethod || []),
    role: Array.from(role || []),
    utmCampaign: Array.from(utmCampaign || []),
    utmContent: Array.from(utmContent || []),
    utmMedium: Array.from(utmMedium || []),
    utmSource: Array.from(utmSource || []),
    utmTerm: Array.from(utmTerm || []),
    query
  }

  const setFilters = (newFilters) => {
    dispatch(setGetParams({
      matches: newFilters.searchIn,
      period: newFilters.dateRange.period,
      start: newFilters.dateRange.start,
      end: newFilters.dateRange.end,
      invitees: newFilters.inviteeEmails,
      event_types: newFilters.eventTypeIds,
      status: newFilters.status,
      scheduling_method: newFilters.schedulingMethod,
      role: newFilters.role,
      utm_campaign: newFilters.utmCampaign,
      utm_content: newFilters.utmContent,
      utm_medium: newFilters.utmMedium,
      utm_source: newFilters.utmSource,
      utm_term: newFilters.utmTerm,
      query: newFilters.query
    }))
  }

  return [
    filters,
    setFilters,
  ]
}

export const Page = () => {
  const { query } = useQueryParams()
  const [filters, setFilters] = useFilters()
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const track = useTrack()
  const { data, isLoading, isError } = useSearchResultsQuery({ query, sortBy, filters })
  const { openOrReplace, isOpen } = useRightRail()
  const [selectedMeetingId, setSelectedMeetingId] = useState(null)

  const changedFilters = changedSearchFilters(filters)

  useTrackPageExit({
    routePattern: '/app/meetings',
    properties: {
      title: 'Meetings',
      search: query
    }
  })

  const openMeetingDetails = (meeting) => {
    openOrReplace('search_meeting_details', {
      selectedMeetingId: meeting.uuid,
    })
    setSelectedMeetingId(meeting.uuid)

    const now = new Date()
    const meetingTime = new Date(meeting?.startTime)
    const timeDifference = meeting?.startTime ? meetingTime - now : null

    const meetingNameHighlight = meeting.highlight?.meeting_name
    const attendeesHighlight = meeting.highlight?.attendees_email || meeting?.highlight?.attendees_name
    const passageHighlight = meeting.passages?.edges?.[0]?.node?.passage_highlight?.highlighted_words

    const queryMatches = [
      meetingNameHighlight && 'meeting_name_highlight',
      attendeesHighlight && 'attendees_highlight',
      passageHighlight && 'passage_highlight',
    ].filter(Boolean)

    track({
      name: TRACKING_EVENT_NAMES.MEETING_INTERACTION,
      properties: {
        action: 'view_meeting_details',
        location: 'meetings_search_results',
        event_uuid: meeting.uuid,
        mi_event_uuid: meeting.meetingIntelligence?.uuid,
        is_calendly_meeting: isCalendlyMeeting(meeting),
        time_difference_in_ms: timeDifference,
        query_matches: queryMatches
      },
    })
  }

  useEffect(() => {
    if (!isOpen && selectedMeetingId) setSelectedMeetingId(null)
  }, [isOpen, selectedMeetingId])

  return (
    <>
      <PageHeader
        title={I18n.t('publisher.meetings.search.results.title')}
        eyebrow={(
          <EyebrowBackButton to="/app/meetings/user/me">
            <ArrowChevronLeftMiniIcon />
            {I18n.t('publisher.meeting_intelligence.details.back_button')}
          </EyebrowBackButton>
        )}
      />
      <Header
        searchQuery={query}
        filters={filters}
        setFilters={setFilters}
        sortOrder={sortBy}
        setSortOrder={setSortBy}
        onSuggestionClick={openMeetingDetails}
      />
      <List
        filters={filters}
        searchQuery={query}
        meetings={data}
        isLoading={isLoading}
        isError={isError}
        selectedMeetingId={selectedMeetingId}
        onSelect={openMeetingDetails}
        changedFilters={changedFilters}
      />
    </>
  )
}
